import { useEffect, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Loader2, RefreshCcw, X } from "lucide-react";
import { getCfoResearchStats } from "@/api/cfoWork";
import type { CfoWorkStatsOperator, CfoWorkStatsPeriod, CfoWorkStatsResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { inputLabel } from "@/lib/cfoLabels";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";

const PERIODS: { value: CfoWorkStatsPeriod; label: string }[] = [
  { value: "week", label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
  { value: "all", label: "All time" },
];

const OUTCOMES: { key: keyof CfoWorkStatsResponse["outcomes"]; label: string; color: string }[] = [
  { key: "qualified", label: "Qualified", color: "bg-emerald-500" },
  { key: "not_qualified", label: "Not qualified", color: "bg-slate-400" },
  { key: "needs_data", label: "Still missing data", color: "bg-amber-400" },
  { key: "needs_review", label: "To review", color: "bg-sky-400" },
  { key: "reevaluating", label: "Being re-scored", color: "bg-indigo-400" },
  { key: "other", label: "Other", color: "bg-slate-200" },
];

function duration(seconds: number): string {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

function Kpi({ value, label, hint }: { value: ReactNode; label: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-900/[0.06] bg-white p-4">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function Card({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="rounded-plane bg-white p-5 shadow-plane">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** CFO research performance: what was worked, what it unblocked, and the
 *  queue. A manager sees the team and can focus on one operator; anyone else
 *  sees their own work. */
export function CfoEnrichmentStats({ slug }: { slug: string }) {
  const [period, setPeriod] = useState<CfoWorkStatsPeriod>("month");
  const [operator, setOperator] = useState<CfoWorkStatsOperator | null>(null);
  const [stats, setStats] = useState<CfoWorkStatsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let live = true;
    setStats(null);
    setFailed(false);
    getCfoResearchStats(slug, period, operator?.user_id)
      .then((value) => live && setStats(value))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [slug, period, operator, generation]);

  const team = stats?.scope === "team" && !operator;
  const outcomeTotal = stats ? OUTCOMES.reduce((sum, o) => sum + stats.outcomes[o.key], 0) : 0;
  const fields = stats ? Object.entries(stats.by_field) : [];
  const maxField = Math.max(1, ...fields.map(([, n]) => n));
  const unblocked = stats ? stats.outcomes.qualified + stats.outcomes.not_qualified : 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-slate-900">
          {operator
            ? `${operator.full_name ?? "Operator"}'s CFO research`
            : stats?.scope === "team"
              ? "Team CFO research"
              : "My CFO research"}
          {operator && (
            <button
              type="button"
              onClick={() => setOperator(null)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-600 hover:bg-slate-200"
            >
              <X className="h-3 w-3" />
              Everyone
            </button>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-all",
                  period === p.value
                    ? "bg-white text-slate-900 shadow-sm-tile"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Refresh"
            onClick={() => setGeneration((n) => n + 1)}
          >
            <RefreshCcw />
          </Button>
        </div>
      </div>

      {failed && (
        <div className="rounded-plane border border-slate-900/[0.06] bg-slate-50/60 p-8 text-center text-sm text-slate-600">
          Couldn&rsquo;t load the CFO research statistics.
        </div>
      )}
      {!stats && !failed && (
        <div role="status" className="flex justify-center py-12 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Kpi value={stats.companies} label="Companies worked" hint={`${stats.completions} closings`} />
            <Kpi value={stats.answers} label="Answers recorded" hint={`${stats.answered} companies answered`} />
            <Kpi value={stats.not_found} label="Can't find" hint="Back with new data" />
            <Kpi value={duration(stats.avg_time_seconds)} label="Avg time per company" />
            <Kpi value={stats.streak} label="Active-day streak" />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card
              title="Where the companies stand now"
              aside={
                <span className="text-xs text-slate-500">
                  {unblocked} of {stats.companies} decided
                </span>
              }
            >
              {outcomeTotal === 0 ? (
                <p className="text-sm text-slate-500">No company worked in this period.</p>
              ) : (
                <>
                  <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
                    {OUTCOMES.map((o) =>
                      stats.outcomes[o.key] ? (
                        <div
                          key={o.key}
                          className={o.color}
                          style={{ width: `${(stats.outcomes[o.key] / outcomeTotal) * 100}%` }}
                          title={`${o.label}: ${stats.outcomes[o.key]}`}
                        />
                      ) : null,
                    )}
                  </div>
                  <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    {OUTCOMES.filter((o) => stats.outcomes[o.key]).map((o) => (
                      <li key={o.key} className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-slate-600">
                          <span className={cn("h-2.5 w-2.5 rounded-full", o.color)} />
                          {o.label}
                        </span>
                        <span className="font-medium tabular-nums text-slate-900">
                          {stats.outcomes[o.key]}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>

            <Card title="The queue now">
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {[
                  ["To research", stats.queue.available],
                  ["To review", stats.queue.review],
                  ...(stats.queue.data ? [["Missing data", stats.queue.data]] : []),
                  ["Held in a session", stats.queue.held],
                  ["Can't find, waiting", stats.queue.parked],
                  ["Being re-scored", stats.queue.reevaluating],
                ].map(([label, value]) => (
                  <li key={label} className="flex items-center justify-between gap-2">
                    <span className="text-slate-600">{label}</span>
                    <span className="font-medium tabular-nums text-slate-900">{value}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          <Card title="Daily closings">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickFormatter={(d: string) =>
                    new Date(d).toLocaleDateString("en-US", {
                      timeZone: "UTC",
                      day: "numeric",
                      month: "short",
                    })
                  }
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar isAnimationActive={false} dataKey="answered" stackId="a" fill="#6366f1" name="Answered" />
                <Bar
                  isAnimationActive={false}
                  dataKey="not_found"
                  stackId="a"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  name="Can't find"
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="Answers by input">
              {fields.length === 0 ? (
                <p className="text-sm text-slate-500">No answer recorded in this period.</p>
              ) : (
                <ul className="space-y-2">
                  {fields.map(([key, count]) => (
                    <li key={key}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{inputLabel(key)}</span>
                        <span className="font-medium tabular-nums text-slate-900">{count}</span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-indigo-500"
                          style={{ width: `${(count / maxField) * 100}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {team && (
              <Card title="Operators">
                {stats.operators.length === 0 ? (
                  <p className="text-sm text-slate-500">Nobody worked in this period.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                          <th className="py-1.5 font-medium">Operator</th>
                          <th className="py-1.5 text-right font-medium">Closed</th>
                          <th className="py-1.5 text-right font-medium">Answers</th>
                          <th className="py-1.5 text-right font-medium">Can&rsquo;t find</th>
                          <th className="py-1.5 text-right font-medium">Avg</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {stats.operators.map((row) => (
                          <tr
                            key={row.user_id}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => setOperator(row)}
                            title="Show this operator's figures"
                          >
                            <td className="py-2">
                              <p className="font-medium text-slate-900">{row.full_name ?? "—"}</p>
                              {row.last_at && (
                                <p className="text-[11px] text-slate-500">
                                  Last {formatDate(row.last_at)}
                                </p>
                              )}
                            </td>
                            <td className="py-2 text-right tabular-nums">{row.completions}</td>
                            <td className="py-2 text-right tabular-nums">{row.answers}</td>
                            <td className="py-2 text-right tabular-nums">{row.not_found}</td>
                            <td className="py-2 text-right tabular-nums">
                              {duration(row.avg_time_seconds)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
